import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type DigestScope = "neighborhood" | "city" | "country";
export type DigestFrequency = "off" | "daily" | "weekly";

export type DigestData = {
  scope: DigestScope;
  locationLabel: string;
  since: string;
  newReports: number;
  resolvedReports: number;
  activeHotspots: number;
  topCategories: { key: string; count: number }[];
  recentReports: any[];
  officialResponses: any[];
};

function scopeFilter(scope: DigestScope, profile: any) {
  if (scope === "neighborhood") {
    return {
      col: "neighborhood",
      val: profile.neighborhood || profile.city,
    };
  }
  if (scope === "city") {
    return { col: "city", val: profile.city };
  }
  return { col: "country", val: profile.country };
}

export const getMyDigest = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const supabase = context.supabase;

    const [{ data: profile }, { data: sub }] = await Promise.all([
      supabase.from("profiles")
        .select("id, country, city, neighborhood")
        .eq("id", context.userId)
        .maybeSingle(),
      supabase.from("digest_subscriptions")
        .select("scope, frequency, day_of_week")
        .eq("user_id", context.userId)
        .maybeSingle(),
    ]);

    const scope = (sub?.scope ?? "city") as DigestScope;
    const frequency = (sub?.frequency ?? "off") as DigestFrequency;
    const filter = scopeFilter(scope, profile ?? {});
    const locationLabel = String(filter.val ?? "your area");

    const since = new Date(Date.now() - 7 * 86400000).toISOString();

    const [{ count: newReports }, { count: resolvedReports }, { count: hotspots }, { data: recent }, { data: responses }] = await Promise.all([
      supabase.from("posts").select("id", { count: "exact", head: true })
        .ilike("location_name", `%${locationLabel}%`)
        .gte("created_at", since)
        .in("post_type", ["issue", "emergency"] as any),
      supabase.from("posts").select("id", { count: "exact", head: true })
        .ilike("location_name", `%${locationLabel}%`)
        .eq("status", "resolved" as any)
        .gte("updated_at", since),
      supabase.from("hotspot_alerts").select("id", { count: "exact", head: true })
        .eq("status", "active" as any)
        .ilike("location_name", `%${locationLabel}%`),
      supabase.from("posts")
        .select("id, title, category, status, severity, location_name, created_at, upvotes, comment_count")
        .ilike("location_name", `%${locationLabel}%`)
        .in("post_type", ["issue", "emergency"] as any)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(10),
      supabase.from("official_responses")
        .select("id, body, created_at, post:posts!inner(id, title, location_name)")
        .ilike("posts.location_name" as any, `%${locationLabel}%`)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(5),
    ]);

    const categoryMap = new Map<string, number>();
    (recent ?? []).forEach((r: any) => categoryMap.set(r.category, (categoryMap.get(r.category) ?? 0) + 1));
    const topCategories = [...categoryMap.entries()]
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      scope,
      frequency,
      locationLabel,
      since,
      newReports: newReports ?? 0,
      resolvedReports: resolvedReports ?? 0,
      activeHotspots: hotspots ?? 0,
      topCategories,
      recentReports: recent ?? [],
      officialResponses: responses ?? [],
    } as DigestData;
  });

export const getDigestSubscription = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("digest_subscriptions")
      .select("scope, frequency, day_of_week")
      .eq("user_id", context.userId)
      .maybeSingle();
    return (data ?? { scope: "city", frequency: "off", day_of_week: 1 }) as { scope: DigestScope; frequency: DigestFrequency; day_of_week: number | null };
  });

export const setDigestSubscription = createServerFn({ method: "POST" })
  .inputValidator((data: { scope: DigestScope; frequency: DigestFrequency; dayOfWeek?: number }) => data)
  .middleware([requireSupabaseAuth])
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("digest_subscriptions")
      .upsert({
        user_id: context.userId,
        scope: data.scope,
        frequency: data.frequency,
        day_of_week: data.dayOfWeek ?? 1,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
    if (error) throw error;
    return { ok: true };
  });
